import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {ReactFlow, MarkerType} from 'reactflow';

import getConfig from 'getConfig';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import EntityMapControls from 'registry/entity-map/EntityMapControls';
import EntityMapEdge from 'registry/entity-map/EntityMapEdge';
import EntityMapSchemaFilter from 'registry/entity-map/EntityMapSchemaFilter';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import sortedBy from 'util/sortedBy';

import {DEFAULT_FIT_VIEW_SETTINGS} from './EntityMapConstants';
import {useEntityMapActorRef, useEntityMapSelector} from './EntityMapMachineProvider';
import EntityNode from './EntityNode';

const ENTITY_MAP_NODE_TYPES = {
  entity: EntityNode,
};

const ENTITY_MAP_EDGE_TYPES = {
  entityEdge: EntityMapEdge,
};

const useStyles = createStyles(() => ({
  container: {
    display: 'flex',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  graphArea: {
    flex: 1,
    height: '100%',
    position: 'relative',
  },
}));

/**
 * Renders a view of the entity map for a given entityId
 */
export default function EntityMapView(): JSX.Element {
  const {classes} = useStyles();
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const isFilteringEnabled = getConfig('REGISTRY_ENABLE_ENTITY_MAP_FILTERING');
  const graphNodesById = useEntityMapSelector((s) => s.context.graphNodesById);
  const graphEdgesById = useEntityMapSelector((s) => s.context.graphEdgesById);
  const actorRef = useEntityMapActorRef();

  const connectedSchemas = useEntityMapSelector((s) => s.context.connectedSchemas);

  const [hiddenSchemas, setHiddenSchemas] = useState<Set<string>>(new Set());

  const entityId = useEntityMapSelector((s) => s.context.entityId);

  const schemaNameToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const schema of connectedSchemas) {
      map.set(schema.name, schema.id);
    }
    const rootNode = graphNodesById.get(entityId);
    if (
      rootNode &&
      !isInaccessible(rootNode.data) &&
      rootNode.data.schemaName != null &&
      rootNode.data.schemaId != null
    ) {
      map.set(rootNode.data.schemaName, rootNode.data.schemaId);
    }
    return map;
  }, [connectedSchemas, graphNodesById, entityId]);

  const previousExcludedRef = useRef<string>('');

  const handleHiddenSchemasChange = useCallback(
    (nextHidden: Set<string>) => {
      setHiddenSchemas(nextHidden);
      const excludedIds = sortedBy(
        Array.from(nextHidden)
          .map((name) => schemaNameToId.get(name))
          .filter((id): id is string => id != null),
        [(id: string) => id]
      );
      const key = excludedIds.join(',');
      if (key !== previousExcludedRef.current) {
        previousExcludedRef.current = key;
        actorRef.send({type: 'updateExcludedSchemas', excludedSchemaIds: excludedIds});
      }
    },
    [actorRef, schemaNameToId]
  );

  const visibleNodeIds = useMemo(() => {
    if (hiddenSchemas.size === 0) {
      return null;
    }
    const ids = new Set<string>();
    for (const node of graphNodesById.values()) {
      if (
        isInaccessible(node.data) ||
        node.data.schemaName == null ||
        !hiddenSchemas.has(node.data.schemaName)
      ) {
        ids.add(node.id);
      }
    }
    return ids;
  }, [graphNodesById, hiddenSchemas]);

  const graphNodes = useMemo(() => {
    const nodes = Array.from(graphNodesById.values());
    if (!visibleNodeIds) {
      return nodes;
    }
    return nodes.filter((node) => visibleNodeIds.has(node.id));
  }, [graphNodesById, visibleNodeIds]);

  const graphEdges = useMemo(() => {
    const edges = Array.from(graphEdgesById.values());
    if (!visibleNodeIds) {
      return edges;
    }
    return edges.filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target));
  }, [graphEdgesById, visibleNodeIds]);

  return (
    <div className={classes.container}>
      {isFilteringEnabled && (
        <EntityMapSchemaFilter
          hiddenSchemas={hiddenSchemas}
          onChangeHiddenSchemas={handleHiddenSchemasChange}
        />
      )}
      <div className={classes.graphArea}>
        <ReactFlow
          {...defaultReactFlowProps}
          defaultEdgeOptions={{
            style: {
              color: Color.GRAY_400,
            },
            markerEnd: {
              type: MarkerType.Arrow,
              width: 20,
              height: 20,
              color: Color.GRAY_400,
            },
            labelStyle: {
              color: Color.GRAY_800,
            },
          }}
          nodes={graphNodes}
          edges={graphEdges}
          nodeTypes={ENTITY_MAP_NODE_TYPES}
          edgeTypes={ENTITY_MAP_EDGE_TYPES}
          onlyRenderVisibleElements={true}
          fitView={true}
          fitViewOptions={DEFAULT_FIT_VIEW_SETTINGS}
          minZoom={0.1}
          deleteKeyCode={null}
          selectionKeyCode={null}
          nodesDraggable={false}
          onNodesChange={(changes) => {
            actorRef.send({type: 'onNodesChange', changes});
          }}
          onEdgesChange={(changes) => {
            actorRef.send({type: 'onEdgesChange', changes});
          }}
        >
          <DefaultBackground />
          <EntityMapControls />
        </ReactFlow>
      </div>
    </div>
  );
}
